import React, { useState, useEffect } from 'react';
import { Button, Input, Select, Card, Statistic, Spin, Empty } from 'antd';
import { PlusOutlined, ReloadOutlined, SearchOutlined, FileTextOutlined, CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { toast } from 'react-toastify';
import * as blogHighlightApi from '../../api/blogHighlightApi';
import BlogHighlightForm from '../../components/admin/blog-highlight/BlogHighlightForm';
import BlogHighlightTable from '../../components/admin/blog-highlight/BlogHighlightTable';
import Modal from '../../components/common/Modal';

const { Option } = Select;

const BlogHighlightManagement = () => {
  const [blogHighlights, setBlogHighlights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingBlogHighlight, setEditingBlogHighlight] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [stats, setStats] = useState({ total: 0, active: 0, inactive: 0 });

  useEffect(() => {
    fetchBlogHighlights();
    fetchStats();
  }, []);

  const fetchBlogHighlights = async () => {
    try {
      setLoading(true);
      const response = await blogHighlightApi.getBlogHighlights();

      if (response.blogHighlights && Array.isArray(response.blogHighlights)) {
        setBlogHighlights(response.blogHighlights);
      } else {
        setBlogHighlights([]);
      }
    } catch (error) {
      console.error('Error fetching blog highlights:', error);
      toast.error('Failed to fetch blog highlights');
      setBlogHighlights([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const data = await blogHighlightApi.getBlogHighlightStats();
      setStats(data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleCreate = () => {
    setEditingBlogHighlight(null);
    setShowForm(true);
  };

  const handleEdit = (blogHighlight) => {
    setEditingBlogHighlight(blogHighlight);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this highlight?')) return;

    try {
      await blogHighlightApi.deleteBlogHighlight(id);
      toast.success('Blog highlight deleted successfully');
      fetchBlogHighlights();
      fetchStats();
    } catch (error) {
      console.error('Error deleting blog highlight:', error);
      const errorMsg = error?.message || error?.msg || 'Failed to delete blog highlight';
      toast.error(errorMsg);
    }
  };

  const handleToggle = async (id) => {
    try {
      await blogHighlightApi.toggleBlogHighlightStatus(id);
      toast.success('Blog highlight status updated');
      fetchBlogHighlights();
      fetchStats();
    } catch (error) {
      console.error('Error toggling blog highlight status:', error);
      toast.error('Failed to update blog highlight status');
    }
  };

  const handleFormSubmit = () => {
    setShowForm(false);
    setEditingBlogHighlight(null);
    fetchBlogHighlights();
    fetchStats();
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingBlogHighlight(null);
  };

  const filteredBlogHighlights = Array.isArray(blogHighlights)
    ? blogHighlights.filter(item => {
        const matchesSearch = item.title?.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesFilter = statusFilter === 'all' ||
                             (statusFilter === 'active' && item.isActive) ||
                             (statusFilter === 'inactive' && !item.isActive);
        return matchesSearch && matchesFilter;
      })
    : [];

  return (
    <>
      <div className="max-w-9xl mx-auto w-full" style={{ fontFamily: 'Rubik, sans-serif' }}>
        <div className="p-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-800" style={{ fontFamily: 'Rubik, sans-serif', color: '#470172' }}>
                Advantage Vidarbha
              </h1>
              <p className="text-gray-600" style={{ fontFamily: 'Rubik, sans-serif' }}>
                Manage the "Highlights of Advantage Vidarbha" blog section on the home page
              </p>
            </div>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleCreate}
              size="large"
              style={{
                backgroundColor: '#470172',
                borderColor: '#470172',
                fontFamily: 'Rubik, sans-serif',
                fontSize: '16px',
                fontWeight: 400
              }}
            >
              Add Highlight
            </Button>
          </div>

          {/* Statistics Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <Card bordered={false} style={{
              borderRadius: '8px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              fontFamily: 'Rubik, sans-serif'
            }}>
              <Statistic
                title={<span style={{ fontFamily: 'Rubik, sans-serif', fontSize: '16px', fontWeight: 400, color: '#000000' }}>Total Highlights</span>}
                value={stats.total}
                prefix={<FileTextOutlined style={{ color: '#470172' }} />}
                valueStyle={{ fontFamily: 'Rubik, sans-serif', fontSize: '28px', fontWeight: 600, color: '#470172' }}
              />
            </Card>
            <Card bordered={false} style={{
              borderRadius: '8px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              fontFamily: 'Rubik, sans-serif'
            }}>
              <Statistic
                title={<span style={{ fontFamily: 'Rubik, sans-serif', fontSize: '16px', fontWeight: 400, color: '#000000' }}>Active</span>}
                value={stats.active}
                prefix={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
                valueStyle={{ fontFamily: 'Rubik, sans-serif', fontSize: '28px', fontWeight: 600, color: '#52c41a' }}
              />
            </Card>
            <Card bordered={false} style={{
              borderRadius: '8px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              fontFamily: 'Rubik, sans-serif'
            }}>
              <Statistic
                title={<span style={{ fontFamily: 'Rubik, sans-serif', fontSize: '16px', fontWeight: 400, color: '#000000' }}>Inactive</span>}
                value={stats.inactive}
                prefix={<CloseCircleOutlined style={{ color: '#ff4d4f' }} />}
                valueStyle={{ fontFamily: 'Rubik, sans-serif', fontSize: '28px', fontWeight: 600, color: '#ff4d4f' }}
              />
            </Card>
          </div>

          {/* Filters */}
          <Card bordered={false} style={{
            borderRadius: '8px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            marginBottom: '24px',
            fontFamily: 'Rubik, sans-serif'
          }}>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Input
                placeholder="Search by title..."
                prefix={<SearchOutlined style={{ color: '#470172' }} />}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                size="large"
                style={{ fontFamily: 'Rubik, sans-serif', fontSize: '16px', fontWeight: 400 }}
              />
              <Select
                value={statusFilter}
                onChange={setStatusFilter}
                size="large"
                style={{ width: '100%', fontFamily: 'Rubik, sans-serif' }}
              >
                <Option value="all">All Status</Option>
                <Option value="active">Active</Option>
                <Option value="inactive">Inactive</Option>
              </Select>
              <Button
                icon={<ReloadOutlined />}
                onClick={fetchBlogHighlights}
                size="large"
                style={{ fontFamily: 'Rubik, sans-serif', fontSize: '16px', fontWeight: 400 }}
              >
                Refresh
              </Button>
            </div>
          </Card>

          {/* Blog Highlights Table */}
          <Card bordered={false} style={{
            borderRadius: '8px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            fontFamily: 'Rubik, sans-serif'
          }}>
            {loading ? (
              <div className="text-center py-12">
                <Spin size="large" />
                <p style={{ marginTop: '16px', fontFamily: 'Rubik, sans-serif', color: '#666' }}>
                  Loading blog highlights...
                </p>
              </div>
            ) : filteredBlogHighlights.length === 0 ? (
              <Empty
                description={
                  <span style={{ fontFamily: 'Rubik, sans-serif', fontSize: '16px', color: '#666' }}>
                    {blogHighlights.length === 0
                      ? 'No highlights yet. Click "Add Highlight" to create one.'
                      : 'No highlights match your filters.'}
                  </span>
                }
              />
            ) : (
              <BlogHighlightTable
                blogHighlights={filteredBlogHighlights}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onToggleStatus={handleToggle}
              />
            )}
          </Card>
        </div>
      </div>

      {/* Form Modal */}
      <Modal
        isOpen={showForm}
        onClose={handleFormCancel}
        title={editingBlogHighlight ? 'Edit Highlight' : 'Add Highlight'}
        width="800px"
      >
        <BlogHighlightForm
          blogHighlight={editingBlogHighlight}
          onSubmit={handleFormSubmit}
          onCancel={handleFormCancel}
        />
      </Modal>
    </>
  );
};

export default BlogHighlightManagement;
